/**
 * Captura de escrita por triggers SQLite (decisão A — ver docs/features/SYNC.md § captura).
 *
 * Cada tabela sincronizada ganha três triggers (`AFTER INSERT/UPDATE/DELETE`) que gravam a
 * mudança POR COLUNA na outbox (`sync_outbox`). O código de domínio (repositories) não muda —
 * qualquer escrita, de qualquer caminho (incluindo backfills e cascades de FK), é capturada
 * pelo banco. O drain (outbox.ts) transforma as linhas da outbox em mensagens com HLC.
 *
 * As specs das tabelas são DERIVADAS do schema Drizzle em runtime (`getTableColumns`) — não há
 * lista paralela pra dessincronizar. Um teste garante que o conjunto de tabelas capturadas +
 * ignoradas cobre o schema inteiro, então tabela nova sem classificação QUEBRA o teste.
 *
 * Guard anti-eco: os triggers só gravam quando `sync_guard` está vazia. Ao aplicar mensagens
 * REMOTAS (ou restaurar um snapshot de bootstrap), o aplicador insere uma linha na guard,
 * escreve, e remove — senão a escrita aplicada re-emitiria como se fosse local (loop).
 *
 * Os triggers NÃO são criados por migração: são instalados quando o sync liga e derrubados
 * quando desliga (`installCaptureTriggers`/`dropCaptureTriggers`). Sem sync, nenhum usuário
 * paga o custo de escrita nem acumula outbox. As tabelas `sync_outbox`/`sync_guard` existem
 * sempre (migração 0020) — vazias, custo zero.
 */

import { getTableColumns, getTableName } from 'drizzle-orm';
import * as schema from '@/db/schema';
import { TOMBSTONE_COLUMN } from './messages';

export interface CaptureTableSpec {
    /** Nome SQL da tabela. */
    table: string;
    /** Colunas SQL capturadas (sem a(s) coluna(s) que formam a identidade da linha). */
    columns: string[];
    /** Colunas SQL que formam a identidade da linha (`id`, ou o par nas junctions). */
    rowKey: string[];
}

/** Tabelas de domínio que sincronizam (ver SYNC.md § 4). */
const CAPTURED = [
    'accounts',
    'creditCards',
    'invoices',
    'invoicePayments',
    'categories',
    'subcategories',
    'transactions',
    'tags',
    'transactionTags',
    'recurringTransactions',
    'recurringTransactionTags',
    'installments',
    'financings',
    'financingAdjustments',
    'people',
    'personalLoans',
    'personalLoanEvents',
    'budgets',
    'budgetCategories',
    'budgetSubcategoryLimits',
    'budgetOrigins',
    'budgetSubcategories',
    'goals',
    'goalDeposits',
    'reservas',
    'reservaMovements',
    'autoCategorizationRules',
] as const;

/** Fica no aparelho (ver SYNC.md § 4) — e as tabelas internas do próprio sync. */
export const NOT_CAPTURED = [
    'settings',
    'pendingNotifications',
    'healthAnalyses',
    'syncOutbox',
    'syncGuard',
    'syncMessages',
    'syncState',
] as const;

/** Junctions sem coluna `id`: a identidade é o par de FKs. */
const COMPOSITE_ROW_KEYS: Record<string, string[]> = {
    transaction_tags: ['transaction_id', 'tag_id'],
    recurring_transaction_tags: ['recurring_id', 'tag_id'],
};

function specFor(exportName: (typeof CAPTURED)[number]): CaptureTableSpec {
    // Acesso computado: quem valida o membro é o TypeScript (`exportName` é
    // `(typeof CAPTURED)[number]`, uma união fechada de nomes que existem no schema).
    const table = schema[exportName];
    const sqlName = getTableName(table);
    const columns = Object.values(getTableColumns(table)).map((c) => c.name);
    const rowKey = COMPOSITE_ROW_KEYS[sqlName] ?? ['id'];
    // Junction (chave composta): as células emitidas SÃO as colunas da chave — sem isso o
    // INSERT não emitiria nada (corpo vazio) e a linha nunca nasceria no outro aparelho.
    // O `row_id` composto identifica; as células carregam os valores pro aplicador inserir
    // sem parsear o row_id.
    const isComposite = sqlName in COMPOSITE_ROW_KEYS;
    return {
        table: sqlName,
        columns: isComposite ? columns : columns.filter((c) => c !== 'id'),
        rowKey,
    };
}

let cachedSpecs: CaptureTableSpec[] | null = null;

/** Specs de captura de todas as tabelas sincronizadas, derivadas do schema Drizzle. */
export function captureSpecs(): CaptureTableSpec[] {
    if (!cachedSpecs) cachedSpecs = CAPTURED.map(specFor);
    return cachedSpecs;
}

// ─── Geração de SQL ──────────────────────────────────────────

const GUARD = 'WHEN (SELECT count(*) FROM `sync_guard`) = 0';

function rowIdExpr(spec: CaptureTableSpec, ref: 'NEW' | 'OLD'): string {
    return spec.rowKey.map((c) => `${ref}.\`${c}\``).join(` || ':' || `);
}

function triggerNames(table: string): { ins: string; upd: string; del: string } {
    return {
        ins: `sync_capture_${table}_ins`,
        upd: `sync_capture_${table}_upd`,
        del: `sync_capture_${table}_del`,
    };
}

/**
 * SQL dos três triggers de captura da tabela.
 *
 * - INSERT: afirma que a linha EXISTE com um tombstone falso (`__deleted__` = 0) e emite
 *   cada coluna NÃO NULA (NULL é o default implícito — não viaja). O tombstone falso é o que
 *   torna um re-INSERT uma RESSURREIÇÃO: o padrão delete-all + reinsert das junctions
 *   (setTransactionTags roda a cada save) apaga e recria o MESMO par; sem o `__deleted__`=0
 *   mais novo, o tombstone do delete venceria e a linha materializaria como apagada no outro
 *   aparelho (a tag sumiria). O motor só ressuscita com tombstone falso mais novo — nunca com
 *   edição de dado —, então essa asserção no INSERT é exatamente o sinal que faltava.
 * - UPDATE: emite cada coluna cujo valor mudou (`IS NOT` trata NULL dos dois lados,
 *   então "setar pra NULL" também viaja). NÃO emite tombstone — edição não ressuscita.
 * - DELETE: emite um único tombstone (`__deleted__` = 1) — inclusive quando o DELETE
 *   veio de um cascade de FK (SQLite dispara triggers em cascades com FK ligada).
 */
export function captureTriggersSql(spec: CaptureTableSpec): string[] {
    const t = triggerNames(spec.table);
    const newRow = rowIdExpr(spec, 'NEW');
    const oldRow = rowIdExpr(spec, 'OLD');

    const insertBody = [
        `INSERT INTO \`sync_outbox\` (table_name, row_id, column_name, value) ` +
            `VALUES ('${spec.table}', ${newRow}, '${TOMBSTONE_COLUMN}', 0);`,
        ...spec.columns.map(
            (c) =>
                `INSERT INTO \`sync_outbox\` (table_name, row_id, column_name, value) ` +
                `SELECT '${spec.table}', ${newRow}, '${c}', NEW.\`${c}\` WHERE NEW.\`${c}\` IS NOT NULL;`,
        ),
    ].join('\n    ');

    const updateBody = spec.columns
        .map(
            (c) =>
                `INSERT INTO \`sync_outbox\` (table_name, row_id, column_name, value) ` +
                `SELECT '${spec.table}', ${newRow}, '${c}', NEW.\`${c}\` WHERE OLD.\`${c}\` IS NOT NEW.\`${c}\`;`,
        )
        .join('\n    ');

    return [
        `CREATE TRIGGER IF NOT EXISTS \`${t.ins}\` AFTER INSERT ON \`${spec.table}\`\n` +
            `${GUARD}\nBEGIN\n    ${insertBody}\nEND;`,
        `CREATE TRIGGER IF NOT EXISTS \`${t.upd}\` AFTER UPDATE ON \`${spec.table}\`\n` +
            `${GUARD}\nBEGIN\n    ${updateBody}\nEND;`,
        `CREATE TRIGGER IF NOT EXISTS \`${t.del}\` AFTER DELETE ON \`${spec.table}\`\n` +
            `${GUARD}\nBEGIN\n    INSERT INTO \`sync_outbox\` (table_name, row_id, column_name, value) ` +
            `VALUES ('${spec.table}', ${oldRow}, '${TOMBSTONE_COLUMN}', 1);\nEND;`,
    ];
}

export function dropCaptureTriggersSql(spec: CaptureTableSpec): string[] {
    const t = triggerNames(spec.table);
    return [t.ins, t.upd, t.del].map((name) => `DROP TRIGGER IF EXISTS \`${name}\`;`);
}

/** SQL de instalação/remoção do conjunto completo (todas as tabelas sincronizadas). */
export function allCaptureTriggersSql(): string[] {
    return captureSpecs().flatMap(captureTriggersSql);
}

export function allDropCaptureTriggersSql(): string[] {
    return captureSpecs().flatMap(dropCaptureTriggersSql);
}

type SqlExecutor = (sql: string) => Promise<{ rows: Record<string, unknown>[] }>;

/**
 * Forma canônica pra comparar o que ESTÁ instalado com o que o schema PEDE. O SQLite guarda em
 * `sqlite_master.sql` o texto original do CREATE, mas reescrito: sem o `IF NOT EXISTS` e sem o
 * `;` final. Normalizar os dois lados (e colapsar espaço em branco) deixa a comparação estável.
 */
function normalizeTriggerSql(sql: string): string {
    return sql
        .replace(/CREATE\s+TRIGGER\s+IF\s+NOT\s+EXISTS/i, 'CREATE TRIGGER')
        .replace(/;\s*$/, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/** SQL dos triggers `sync_capture_*` VIGENTES no banco, por nome. */
async function installedCaptureTriggers(execute: SqlExecutor): Promise<Map<string, string>> {
    // `_` é curinga no LIKE — sem o ESCAPE, `sync_capture_%` casaria nomes alheios.
    const result = await execute(
        "SELECT name, sql FROM sqlite_master WHERE type = 'trigger' AND name LIKE 'sync\\_capture\\_%' ESCAPE '\\';",
    );
    const installed = new Map<string, string>();
    for (const row of result?.rows ?? []) {
        if (typeof row.name === 'string' && typeof row.sql === 'string') {
            installed.set(row.name, normalizeTriggerSql(row.sql));
        }
    }
    return installed;
}

/**
 * Instala os triggers de captura. Chamado quando o sync LIGA — e, na prática, a CADA ciclo
 * oportunístico: `maybeSyncNow` chama `enableSync()`, e o poll de 45s do `app/_layout` passa
 * folgado pelo throttle de 30s. Por isso o caminho comum tem que ser barato E sem janela.
 *
 * Os triggers gravam a lista de colunas VIGENTE quando foram criados, e o CREATE é
 * `IF NOT EXISTS`: numa base que já tinha sync ligado, uma coluna adicionada por migração
 * (ex.: `financings.kind`, migração 0030) nunca entraria na outbox — a mudança sumia entre
 * aparelhos, em silêncio. Daí a necessidade de recriar.
 *
 * Mas recriar INCONDICIONALMENTE trocava um vazamento silencioso por outro: entre o DROP e o
 * CREATE não existe trigger nenhum, e os repositórios não pegam o lock do sync (nada em
 * db/repositories/ usa `withSyncLock`), então um save do usuário intercala entre dois `await`
 * do install e não é capturado. A cada ~45s, para sempre. Ver captureInstall.test.ts.
 *
 * Então recria só o que ficou velho, tabela por tabela, comparando com o `sqlite_master` — que
 * é a verdade do banco, não um registro paralelo que possa divergir. No caminho comum não sai
 * DDL nenhum. Sobra uma janela de 6 statements na tabela que mudou, uma vez por atualização
 * que mexa no schema dela; fechá-la de vez exigiria que toda escrita passasse pelo lock.
 */
export async function installCaptureTriggers(execute: SqlExecutor): Promise<void> {
    // Limpa qualquer linha ÓRFÃ da guard antes de instalar: se o processo morreu entre a
    // suspensão (INSERT na guard) e a limpeza (DELETE) de um withCaptureSuspended anterior,
    // a linha sobrevive no .db e os triggers ficariam MUDOS — nenhuma escrita local seria
    // capturada, em silêncio. (op-sqlite transaction() não faz rollback, então não dá pra
    // confiar em limpeza transacional — ver docs/features/SYNC.md.) Fica FORA do atalho
    // abaixo: é o outro trabalho do install, e some junto com o DDL se ficar dentro.
    await execute('DELETE FROM `sync_guard`;');

    const installed = await installedCaptureTriggers(execute);
    const wanted = new Set<string>();

    for (const spec of captureSpecs()) {
        const sqls = captureTriggersSql(spec);
        const t = triggerNames(spec.table);
        const names = [t.ins, t.upd, t.del]; // mesma ordem de captureTriggersSql
        names.forEach((name) => wanted.add(name));

        const current = names.every((name, i) => installed.get(name) === normalizeTriggerSql(sqls[i]));
        if (current) continue;

        // Drop + create da MESMA tabela em sequência: a janela sem trigger fica restrita a ela.
        for (const sql of dropCaptureTriggersSql(spec)) await execute(sql);
        for (const sql of sqls) await execute(sql);
    }

    // Trigger de tabela que saiu do conjunto capturado continuaria gravando numa outbox que
    // ninguém drena para ela.
    for (const name of installed.keys()) {
        if (!wanted.has(name)) await execute(`DROP TRIGGER IF EXISTS \`${name}\`;`);
    }
}

/** Remove os triggers (idempotente). Chamado quando o sync DESLIGA. */
export async function dropCaptureTriggers(execute: SqlExecutor): Promise<void> {
    for (const sql of allDropCaptureTriggersSql()) await execute(sql);
}

/**
 * Executa `fn` com a captura suspensa (guard anti-eco) — para aplicar mensagens remotas ou
 * restaurar snapshot sem re-emitir as escritas como locais. NÃO aninhar.
 */
export async function withCaptureSuspended<T>(execute: SqlExecutor, fn: () => Promise<T>): Promise<T> {
    await execute('INSERT INTO `sync_guard` (id) VALUES (1);');
    try {
        return await fn();
    } finally {
        await execute('DELETE FROM `sync_guard`;');
    }
}
